<template>
  <div class="workflow">
    <div class="step-one" v-if="workflow.step === Workflow.STEP_ONE">
      <button class="col-12 workflow-branch" @click="workflow.newSemester()">
        <h2>New Semester Reset</h2>
        <p>
          Provide a list of students, and amount of credits to set their allowances.
          If an existing student is not in the provided list, their allowance will be set to zero.
        </p>
      </button>
      <button class="col-12 workflow-branch" @click="workflow.midSemesterCorrection()">
        <h2>Mid Semester Correction</h2>
        <p>
          Provide a list of students, the amount of credits, 
          the current semester start date, and how to handle missing 
          students to update allowances.
        </p>
      </button>
    </div>
    <div class="step-two" v-if="workflow.step === Workflow.AS_OF">
      <h2>
        When was the Current Semester's start date?
      </h2>
      <p>
        We will set the student's allowance to the amounts you specify as of this date.
      </p>
      <p>
        If a student has used credits since this date, those credits will be counted 
        against this updated allowance.
      </p>
      <div class="dates">
        <label>
          <span class="sr-only">As of</span>
          <input 
            type="date" 
            :value="workflow.asOf.format('YYYY-MM-DD')"
            @input="workflow.asOf = $event.target.value ?  moment($event.target.value) : moment()"
            :max="tomorrow" />
        </label>
      </div>
      <button @click="workflow.next()" :disabled="workflow.asOf === null">
        Next
      </button>
    </div>
    <div class="step-three" v-if="workflow.step === Workflow.HANDLE_MISSING">
      <h2>
        What should we do with allowances that are missing from the list you provide?
      </h2>
      <button 
        :class="workflow.zeroOutMissing === false ? 'selected' : ''" 
        @click="workflow.zeroOutMissing = false">
        Leave them alone
      </button>
      <button 
        :class="workflow.zeroOutMissing === true ? 'selected' : ''" 
        @click="workflow.zeroOutMissing = true">
        Set them to zero
      </button>
      <button @click="workflow.next()" :disabled="workflow.zeroOutMissing === null">
        Next
      </button>
    </div>
    <div class="step-four" v-if="workflow.step === Workflow.INDIVIDUAL_CREDITS">
      <h2>
        Enter the number of individual credits each student should be allowed.
      </h2>
      <input
        type="number"
        v-model="workflow.individualCredits" 
        min="0" />
      <button 
        @click="workflow.next()" 
        :disabled="workflow.individualCredits === null || workflow.individualCredits < 0">
        Next
      </button>
    </div>
    <div class="step-five" v-if="workflow.step === Workflow.WORKSHOP_CREDITS">
      <h2>
        Enter the number of workshop credits each student should be allowed.
      </h2>
      <input
        type="number"
        v-model="workflow.workshopCredits" 
        min="0" />
      <button 
        @click="workflow.next()" 
        :disabled="workflow.workshopCredits === null || workflow.workshopCredits < 0">
        Next
      </button>
    </div>
    <div class="step-six" v-if="workflow.step === Workflow.UPLOAD_FILE">
      <h2>
        Upload your file of user email addresses.
      </h2>
      <div>
        <b-form-file
          id="identifiersFile"
          @change="fileUploaded"
          name="identifiersFile"
          accept=".csv"
          placeholder="Select a CSV file"
          drop-placeholder="Drop file here..."
          :state="Boolean(workflow.identifiers.length)"
        />
      </div>
      <div class="error-container">
        <p v-for="error of workflow.errors" :key="error.email" >
          {{ error.email }} is not formatted properly.
        </p>
      </div>
      <button 
        @click="whatIfCheck()" 
        :disabled="workflow.identifiers.length === 0 || workflow.errors.length > 0" >
        Next
      </button>
    </div>
    <div class="step-seven" v-if="workflow.step === Workflow.WHAT_IF">
      <h2>What If Check</h2>
      <loader v-if="workflow.whatIf === null" />
      <template v-else>
        <table>
          <tr>
            <td><b>Individual Credits:</b></td>
            <td>{{workflow.individualCredits}}</td>
          </tr>
          <tr>
            <td><b>Workshop Credits:</b></td>
            <td>{{workflow.workshopCredits}}</td>
          </tr>
          <tr>
            <td><b>Allowance to take effect as of:</b></td>
            <td>{{workflow.asOf.format("MM / DD / YYYY")}}</td>
          </tr>
          <tr>
            <td><b>Missing Allowances:</b></td>
            <td>
              {{workflow.zeroOutMissing ? "Set to zero" : "Leave them alone"}}
            </td>
          </tr>
        </table>
        <b>Results:</b>
        <ul>
          <li> {{workflow.whatIf.generateCount}} allowances will be generated. </li>
          <li> {{workflow.whatIf.updateCount}} allowances will be updated. </li>
          <li> {{workflow.whatIf.ignoreCount}} allowances were provided, but no change is needed and will be ignored. </li>
          <li> {{workflow.whatIf.zeroCount}} allowances already exist and will be set to zero. </li>
        </ul>
        <div class="footer">
          <button @click="$modal.close()" >
            Cancel
          </button>
          <button @click="finalSave()" >
            Upload Allowances
          </button>
        </div>

      </template>
    </div>
    <div class="step-eight" v-if="workflow.step === Workflow.SAVING">
      <h2>Uploading Allowances</h2>
      <loader />
    </div>
    <div class="step-nine" v-if="workflow.step === Workflow.COMPLETE">
      <div>
        <h2>Complete</h2>
        <div v-if="workflow.errors.length" class="error-container">
          <p v-for="error of workflow.errors" :key="error" >
            {{ error }}
          </p>
        </div>
        <div v-else>
          <p>
            Changes were applied successfully!
          </p>
        </div>
      </div>
      <button @click="$modal.close()" >
        Close
      </button>
    </div>
  </div>
</template>

<script>
import InputDatepicker from '@/components/inputs/InputDatepicker.vue'
import InputText from '@/components/inputs/InputText.vue'
import InputRadio from '@/components/inputs/InputRadio.vue'
import Loader from '@/components/Loader.vue'
import Workflow from './workflow'
import FormComponent from '@/components/FormComponent.vue'
import moment from 'moment'

export default {
  components: { 
    InputDatepicker, 
    InputText,
    InputRadio,
    Loader,
    FormComponent,
  },
  
  props: {
    schoolId: {
      type: Number,
      require: true,
    }
  },
  data() {
    return { 
      Workflow: Workflow,
      moment: moment,
      workflow: new Workflow(this.schoolId, this.$http),
      tomorrow: moment().format('YYYY-MM-DD'),
    }
  },
  methods:{
    async finalSave() {
      this.workflow.next();
      await this.workflow.uploadAllowances();
      this.workflow.next();
    },
    whatIfCheck() {
      this.workflow.getWhatIf();
      this.workflow.next();
    },
    async fileUploaded(event){
      try {

        if(event.dataTransfer)
          await this.workflow.parseFile(event.dataTransfer.files[0])
        else
          await this.workflow.parseFile(event.target.files[0])
          
      } catch(ex) {
        console.error(ex)
        this.$store.commit('setGlobalAlert', {
          msg: `Failed to read CSV. Please be sure the file matches the expected format.`,
          error: true
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
div.workflow {
  color: $black;
  h2 {
    color:$black;
    font-family: 'Open Sans', sans-serif
  }
  p {
    font-style: italic;
  }
  button {
    background-color:$white;
    border-radius: 10px;
  }
  button:hover {
    background-color:$light-gray;
    box-shadow: 0 2px 4px rgb(0 0 0 / 24%);
  }
}
.step-one {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 0px 10px;

  button.workflow-branch {
    margin-bottom: 10px;
  }
}

.step-two {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 10px 10px;

  .dates {
    margin-bottom:10px;
    width: 150px;
    border: 1px $black;
    color-scheme: light;
    background: $white;
    border-radius: 8px;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

    label {
      margin: 0;
    }

    input {
      border: 0;
      background: transparent;
      line-height: 2;
      font-size: 14px;
      font-weight: 500;
      height: 46px;
      padding: 0 16px;
    }

    svg {
      opacity: 0.8;
    }

    &:hover {
      box-shadow: 0 2px 4px rgb(0 0 0 / 18%);
      border-color: #bababa;
    }
  }
}

.step-three {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 0px 10px;

  button {
    margin-bottom: 10px;
  }

  .selected {
    background-color: $light-gray;
  }
}

.step-four {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 10px 10px;
  input {
    border-radius: 10px;
    width: 150px;
    margin-top: 10px;
    margin-bottom: 10px;
    text-align: center;
  }
}

.step-five {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 10px 10px;
  input {
    border-radius: 10px;
    width: 150px;
    margin-top: 10px;
    margin-bottom: 10px;
    text-align: center;
  }
}

.step-six {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 10px 10px;
  
  .error-container {
    max-height: 300px;
    overflow-y: auto;
  }
  button {
    margin-top: 10px;
  }
}

.step-seven {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 10px 10px;
  
  table {
    margin-bottom:10px;
  }

  .footer {
    display:flex;
    button {
      flex: 1;
    }
  }
}

.step-eight {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 10px 10px;
  
}

.step-nine {
  display:flex;
  flex-direction: column;
  padding: 10px 10px 10px 10px;
  justify-content: space-between;
  
  height:300px;
}
</style>